import React, { useEffect } from "react";
import { useV2Theme } from "../../theme/ThemeProvider";
import { v2cx } from "../../theme/tokens";

/**
 * Mobile bottom sheet with dimmer. Snap heights: peek | half | full.
 */
export default function MobileBottomSheet({
  open,
  onClose,
  title,
  children,
  footer,
  height = "half",
  ariaLabel,
}) {
  const { tokens } = useV2Theme();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    // Do not set body overflow:hidden — that breaks iOS nested scroll in Ask
    // and can fight the shell's dp-app-shell-lock height containment.
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const heightClass =
    height === "full"
      ? "max-h-[92dvh] h-[92dvh]"
      : height === "peek"
        ? "max-h-[40dvh]"
        : "max-h-[70dvh]";

  return (
    <div className="fixed inset-0 z-[60] lg:hidden" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || "Sheet"}
        className={v2cx(
          "absolute inset-x-0 bottom-0 flex flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl",
          "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          heightClass,
          tokens.mobileNav?.sheet?.includes("slate-900") &&
            "border-slate-800 bg-slate-900"
        )}
      >
        <div className="mx-auto mt-2 mb-1 h-1 w-10 shrink-0 rounded-full bg-slate-300/80" aria-hidden />
        {title ? (
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
            <h2 className="truncate text-sm font-semibold text-slate-900">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-slate-500 active:bg-slate-100"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-slate-100 px-4 pt-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
